<script setup>
import {
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";

import Chart from "chart.js/auto";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },

  height: {
    type: String,
    default: "300",
  },

  title: {
    type: String,
    default: "",
  },

  description: {
    type: String,
    default: "",
  },

  chart: {
    type: Object,
    required: true,
  },
});

const canvas = ref(null);

let chartInstance = null;


/*
|--------------------------------------------------------------------------
| Destroy chart
|--------------------------------------------------------------------------
*/

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}


/*
|--------------------------------------------------------------------------
| Create chart
|--------------------------------------------------------------------------
*/

async function renderChart() {
  await nextTick();

  if (!canvas.value) {
    return;
  }

  const labels = props.chart?.labels ?? [];
  const datasets = props.chart?.datasets ?? [];

  // Brak danych
  if (!Array.isArray(labels) || !Array.isArray(datasets)) {
    return;
  }

  if (datasets.length === 0) {
    destroyChart();
    return;
  }

  // Jeżeli nie mamy jeszcze żadnych punktów
  if (labels.length === 0) {
    destroyChart();
    return;
  }

  destroyChart();

  const ctx = canvas.value.getContext("2d");

  if (!ctx) {
    return;
  }


  /*
  |--------------------------------------------------------------------------
  | Gradients
  |--------------------------------------------------------------------------
  */

  const gradientStroke1 = ctx.createLinearGradient(
    0,
    230,
    0,
    50
  );

  gradientStroke1.addColorStop(
    1,
    "rgba(203,12,159,0.2)"
  );

  gradientStroke1.addColorStop(
    0.2,
    "rgba(72,72,176,0.0)"
  );

  gradientStroke1.addColorStop(
    0,
    "rgba(203,12,159,0)"
  );


  const gradientStroke2 = ctx.createLinearGradient(
    0,
    230,
    0,
    50
  );

  gradientStroke2.addColorStop(
    1,
    "rgba(20,23,39,0.2)"
  );

  gradientStroke2.addColorStop(
    0.2,
    "rgba(72,72,176,0.0)"
  );

  gradientStroke2.addColorStop(
    0,
    "rgba(20,23,39,0)"
  );


  /*
  |--------------------------------------------------------------------------
  | Dataset configuration
  |--------------------------------------------------------------------------
  */

  const chartDatasets = datasets.map((dataset, index) => ({
    label: dataset.label ?? `Seria ${index + 1}`,

    data: Array.isArray(dataset.data)
      ? dataset.data.map(value => Number(value ?? 0))
      : [],

    tension: 0.4,

    borderWidth: 3,

    pointRadius: 0,

    pointHoverRadius: 5,

    borderColor:
      index === 0
        ? "#4BB543"
        : "#3A416F",

    backgroundColor:
      index === 0
        ? gradientStroke1
        : gradientStroke2,

    fill: true,

    maxBarThickness: 6,
  }));


  /*
  |--------------------------------------------------------------------------
  | Chart.js
  |--------------------------------------------------------------------------
  */

  chartInstance = new Chart(ctx, {
    type: "line",

    data: {
      labels,
      datasets: chartDatasets,
    },

    options: {
      responsive: true,

      maintainAspectRatio: false,

      interaction: {
        intersect: false,
        mode: "index",
      },

      plugins: {
        legend: {
          display: false,
        },

        tooltip: {
          enabled: true,
        },
      },

      scales: {
        y: {
          beginAtZero: true,

          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
          },

          ticks: {
            display: true,
            padding: 10,
            color: "#67748e",

            font: {
              size: 11,
              family: "Open Sans",
              style: "normal",
              lineHeight: 2,
            },
          },
        },

        x: {
          grid: {
            drawBorder: false,
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
          },

          ticks: {
            display: true,
            color: "#ccc",
            padding: 10,

            font: {
              size: 11,
              family: "Open Sans",
              style: "normal",
              lineHeight: 2,
            },

            /*
             * Przy 30 dniach pokazujemy co kilka etykiet,
             * żeby nie nachodziły na siebie.
             */
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
      },
    },
  });
}


/*
|--------------------------------------------------------------------------
| Initial render
|--------------------------------------------------------------------------
*/

onMounted(() => {
  renderChart();
});


/*
|--------------------------------------------------------------------------
| React to API data changes
|--------------------------------------------------------------------------
*/

watch(
  () => props.chart,
  () => {
    renderChart();
  },
  {
    deep: true,
  }
);


/*
|--------------------------------------------------------------------------
| Cleanup
|--------------------------------------------------------------------------
*/

onBeforeUnmount(() => {
  destroyChart();
});
</script>


<template>
  <div class="card z-index-2">

    <div class="pb-0 card-header mb-0">

      <h6>
        {{ props.title }}
      </h6>

      <p
        v-if="props.description"
        class="text-sm"
        v-html="props.description"
      ></p>

    </div>


    <div class="p-3 card-body">

      <div
        class="chart"
        :style="{
          position: 'relative',
          height: `${props.height}px`
        }"
      >

        <canvas
          ref="canvas"
          :id="props.id"
          class="chart-canvas"
        ></canvas>

      </div>

    </div>

  </div>
</template>